import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from './shared/services/user.service';
import { ApiService } from './shared/services/api.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AppConstants } from './shared/constants/app.contants';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SessionService } from './shared/services/session.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  forceChangeForm: FormGroup;
  forceChangeError: string = '';
  forceChangeSubmitting = false;

  constructor(
    public userService: UserService,
    private apiService: ApiService,
    private fb: FormBuilder,
    private router: Router,
    private http: HttpClient,
    private jwtHelper: JwtHelperService,
    private sessionService: SessionService
  ) {
    this.forceChangeForm = this.fb.group({
      currentPassword: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required]
    });
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.userService.isUserAuthenticated) {
          this.sessionService.startAutoRefresh();
        }
      });
  }
  title = 'bidaya';

  ngOnInit() {
    this.userService.initializeFromLocalStorage();
    this.validateSession();
  }

  private validateSession(): void {
    const token = this.userService.token || localStorage.getItem('QNBNToken');
    if (!token) {
      return;
    }

    if (this.jwtHelper.isTokenExpired(token)) {
      this.sessionService.refreshSession().subscribe(refreshed => {
        if (!refreshed) {
          this.sessionService.endSession();
        }
      });
      return;
    }

    this.http.get<any>(`${environment.API_URL}/${environment.APIs_PREFIX}/User/ValidateSession`, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'X-Skip-Retry': 'true',
        'X-Skip-Session-Refresh': 'true'
      })
    }).subscribe({
      next: response => {
        if (response?.isSuccess !== true || response?.data !== true) {
          this.sessionService.endSession();
          return;
        }

        this.sessionService.startAutoRefresh();
      },
      error: () => this.sessionService.endSession()
    });
  }

  get showForceChangeModal(): boolean {
    return !!this.userService.isUserAuthenticated && this.userService.mustChangePassword;
  }

  onSubmitForceChange(): void {
    this.forceChangeError = '';
    const v = this.forceChangeForm.value;
    if (!v.currentPassword || !v.password || !v.confirmPassword) {
      this.forceChangeError = 'All fields are required.';
      return;
    }
    if (v.password !== v.confirmPassword) {
      this.forceChangeError = 'New password and confirm password do not match.';
      return;
    }
    const strong = /[A-Z]/.test(v.password) && /[a-z]/.test(v.password) && /[0-9]/.test(v.password) && /[^a-zA-Z0-9]/.test(v.password);
    if (!strong) {
      this.forceChangeError = 'Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character.';
      return;
    }
    this.forceChangeSubmitting = true;
    this.apiService.setBaseUrl(environment.API_URL);
    const payload = {
      Email: this.userService.email,
      CurrentPassword: v.currentPassword,
      Password: v.password,
      ConfirmPassword: v.confirmPassword
    };
    const req$ = this.apiService.post(
      AppConstants.CONTROLLERS.ACCOUNT,
      AppConstants.ACTIONS.CHANGE_PASSWORD,
      true,
      environment.APIs_PREFIX,
      payload
    );
    lastValueFrom(req$).then((res: any) => {
      if (res?.isSuccess) {
        this.userService.clearMustChangePassword();
        this.forceChangeForm.reset();
        this.forceChangeError = '';
      } else {
        this.forceChangeError = res?.errorMessageEn || 'Failed to change password.';
      }
    }).catch(() => {
      this.forceChangeError = 'Failed to change password. Please try again.';
    }).finally(() => {
      this.forceChangeSubmitting = false;
    });
  }

  onLogoutFromForceChange(): void {
    this.userService.logout();
    this.forceChangeForm.reset();
    this.forceChangeError = '';
    this.router.navigate([AppConstants.ROUTES.LOGIN]);
  }
}


